import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { dashboardApi, areaManagersApi, brandsApi, areasApi, aiApi } from '../lib/api';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Checkbox } from '../components/ui/checkbox';
import { Button } from '../components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import ProfitabilitySection from '../components/ProfitabilitySection';
import TopCostsChart from '../components/TopCostsChart';
import HealthBadge from '../components/HealthBadge';
import { healthFromKpiMap } from '../lib/health';
import { TrendingUp, TrendingDown, Store as StoreIcon, X, Info } from 'lucide-react';
import { AIInsightsPanel } from '../components/AIInsightsPanel';
import { SortTh, useSort } from '../components/SortTh';
import { SavedViewsControl } from '../components/SavedViewsControl';
import { CopyLinkButton } from '../components/CopyLinkButton';
// SeriesTooltipContent + Recharts BarChart/Bar/Axis/etc. erano usati nel
// vecchio bar-chart per-negozio della KpiAggregatedTabs (rimosso nel
// refactor 2026-05-11 idea_389945efd628 a favore della tabella sparkline+
// semaforo). Sono recuperabili da git history se servisse il toggle "vista
// classica".
import { semaphoreFromPct, semaphoreFromPp, SEMAPHORE_TEXT } from '../lib/semaphore';
import { usePersistedPeriod } from '../lib/usePersistedPeriod';
import { useUrlState } from '../lib/useUrlState';
import DateRangePicker from '../components/DateRangePicker';
import { rangeToPeriod, periodToRange, isCanonicalRange } from '../lib/dateRangeUtils';
import { parseISO, format as dfFormat } from 'date-fns';
import { it as itLocale } from 'date-fns/locale';
// Recharts non più importati: la nuova `KpiAggregatedTabs` usa SVG
// inline per sparkline e barre semplici (vedi `SparklineRow`). Riaggiungi
// l'import se serve un grafico Recharts in futuro.

const MONTHS = ['Gennaio','Febbraio','Marzo','Aprile','Maggio','Giugno','Luglio','Agosto','Settembre','Ottobre','Novembre','Dicembre'];
const CHART_COLORS = ['#0F172A','#334155','#64748B','#94A3B8','#CBD5E1','#E2E8F0','#1E293B','#475569','#6B7280','#9CA3AF'];

function fmt(val, format) {
  if (val == null) return '-';
  if (format === 'currency') return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(val);
  if (format === 'percentage') return `${val.toFixed(1)}%`;
  if (format === 'hours') return `${val.toFixed(1)}h`;
  if (format === 'integer') return Math.round(val).toLocaleString('it-IT');
  return val.toLocaleString('it-IT', { maximumFractionDigits: 2 });
}

function SemDot({ status }) {
  return <div className={`w-2 h-2 rounded-full inline-block sem-dot-${status}`} />;
}

// Formule per i KPI derivati: tooltip esplicativi accanto al label.
const KPI_FORMULAS = {
  hourly_revenue: 'Ricavi totali / Ore disponibili',
  avg_ticket: 'Ricavi totali / Numero servizi',
  occupancy_pct: 'Ore prenotate / Ore disponibili',
  new_client_pct: 'Nuovi clienti / Totale clienti',
  returning_client_rate: 'Clienti abituali / Totale clienti',
};

export default function AreaDashboardPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [areaManagers, setAreaManagers] = useState([]);
  const [brands, setBrands] = useState([]);
  const [areas, setAreas] = useState([]);
  const [selectedAM, setSelectedAM] = useUrlState('am', 'all');
  const [selectedBrand, setSelectedBrand] = useUrlState('brand', 'all');
  const [selectedArea, setSelectedArea] = useUrlState('area', 'all');
  const [selectedStoreIds, setSelectedStoreIds] = useUrlState('stores', [], {
    parse: (s) => s ? s.split(',').filter(Boolean) : [],
    serialize: (arr) => Array.isArray(arr) ? arr.join(',') : '',
    isDefault: (arr) => !Array.isArray(arr) || arr.length === 0,
  });
  const [availableStores, setAvailableStores] = useState([]);
  const [storeFilterOpen, setStoreFilterOpen] = useState(false);
  const [storeFilterQuery, setStoreFilterQuery] = useState('');
  // periodo: '1'..'12' per mesi, 'Q1'..'Q4' per trimestri, 'YEAR' per annuale.
  // Resta la source-of-truth per i deep-link cross-pagina e i widget mensili
  // (MonthSelectorRich nel negozio); il `DateRangePicker` può però scegliere
  // un range custom che non corrisponde a nessun period canonico → in quel
  // caso `customRange` non è null e il backend riceve `date_from/date_to`.
  const { period, setPeriod, year, setYear } = usePersistedPeriod('1', 2026);
  const [customRange, setCustomRange] = useState(null); // { from, to } se non canonico
  const [refreshKey, setRefreshKey] = useState(0);
  const [lastRefresh, setLastRefresh] = useState(null);
  const { sort, toggleSort } = useSort('revenue', 'desc');

  const triggerRefresh = useCallback(() => setRefreshKey(k => k + 1), []);

  useEffect(() => {
    Promise.all([areaManagersApi.list(), brandsApi.list(), areasApi.list()])
      .then(([amRes, bRes, aRes]) => { setAreaManagers(amRes.data); setBrands(bRes.data); setAreas(aRes.data); });
  }, []);

  // Auto-refresh quando la tab torna visibile o quando un'altra parte dell'app
  // emette l'evento 'storeops:data-changed' (es. save budget / costi / dettagli).
  useEffect(() => {
    const onVisible = () => { if (document.visibilityState === 'visible') triggerRefresh(); };
    const onDataChanged = () => triggerRefresh();
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('storeops:data-changed', onDataChanged);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('storeops:data-changed', onDataChanged);
    };
  }, [triggerRefresh]);

  // Sostituisce il singolo `month` state: calcolo params dal `period`
  const isAnnual = period === 'YEAR';
  const isQuarter = period.startsWith('Q');
  const month = useMemo(() => (isAnnual || isQuarter) ? null : Number(period), [period, isAnnual, isQuarter]);
  const quarter = useMemo(() => isQuarter ? Number(period.slice(1)) : null, [period, isQuarter]);
  // Effettivo date range usato per la fetch + label. Se `customRange` è
  // settato, vincono i suoi `from/to`; altrimenti deriviamo il range
  // canonico dal `period` corrente.
  const effectiveRange = useMemo(() => {
    if (customRange?.from && customRange?.to) return customRange;
    return periodToRange(period, year) || null;
  }, [customRange, period, year]);
  const periodLabel = useMemo(() => {
    if (customRange?.from && customRange?.to && !isCanonicalRange(customRange.from, customRange.to)) {
      try {
        const f = parseISO(customRange.from);
        const t = parseISO(customRange.to);
        return `${dfFormat(f, 'd MMM', { locale: itLocale })} → ${dfFormat(t, 'd MMM yy', { locale: itLocale })}`;
      } catch {
        return `${customRange.from} → ${customRange.to}`;
      }
    }
    if (isAnnual) return `Anno ${year}`;
    if (isQuarter) return `Q${quarter} ${year}`;
    return `${MONTHS[month - 1]} ${year}`;
  }, [customRange, isAnnual, isQuarter, month, quarter, year]);

  // Reset store selection when the context (Area/Brand/AM) changes — la pool dei negozi disponibili cambia.
  // Skippa il primo render: se l'URL contiene già `?stores=...` insieme ai filtri AM/Brand/Area
  // non vogliamo cancellarlo prima ancora che l'utente abbia interagito.
  const isFirstFilterRender = useRef(true);
  useEffect(() => {
    if (isFirstFilterRender.current) {
      isFirstFilterRender.current = false;
      return;
    }
    setSelectedStoreIds([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAM, selectedBrand, selectedArea]);

  // AI fetchers stable: il panel ora carica solo la cache al mount e rigenera
  // esclusivamente on-demand su click utente.
  const aiCacheFetcher = useCallback(
    () => aiApi.areaInsights({ month, year, quarter, annual: isAnnual, area_manager_id: selectedAM, brand_id: selectedBrand, area: selectedArea, store_ids: selectedStoreIds, cache_only: true }),
    [month, year, quarter, isAnnual, selectedAM, selectedBrand, selectedArea, selectedStoreIds]
  );
  const aiGenerateFetcher = useCallback(
    () => aiApi.areaInsights({ month, year, quarter, annual: isAnnual, area_manager_id: selectedAM, brand_id: selectedBrand, area: selectedArea, store_ids: selectedStoreIds, force: false }),
    [month, year, quarter, isAnnual, selectedAM, selectedBrand, selectedArea, selectedStoreIds]
  );
  const aiForceFetcher = useCallback(
    () => aiApi.areaInsights({ month, year, quarter, annual: isAnnual, area_manager_id: selectedAM, brand_id: selectedBrand, area: selectedArea, store_ids: selectedStoreIds, force: true }),
    [month, year, quarter, isAnnual, selectedAM, selectedBrand, selectedArea, selectedStoreIds]
  );
  const aiPdfFetcher = useCallback(
    () => aiApi.downloadAreaPdf({ month, year, quarter, annual: isAnnual, area_manager_id: selectedAM, brand_id: selectedBrand, area: selectedArea, store_ids: selectedStoreIds }),
    [month, year, quarter, isAnnual, selectedAM, selectedBrand, selectedArea, selectedStoreIds]
  );

  useEffect(() => {
    setLoading(true);
    const params = { year };
    // Range mode: passa sempre `date_from`/`date_to` quando definiti — il
    // backend autodetecta i range canonici e sceglie la pipeline mensile vs
    // daily. Niente snap lato client (rimosso refactor 2026-05-13).
    if (effectiveRange?.from && effectiveRange?.to) {
      params.date_from = effectiveRange.from;
      params.date_to = effectiveRange.to;
    } else if (isAnnual) {
      params.annual = true;
    } else if (quarter) {
      params.quarter = quarter;
    } else {
      params.month = month;
    }
    if (selectedAM !== 'all') params.area_manager_id = selectedAM;
    if (selectedBrand !== 'all') params.brand_id = selectedBrand;
    if (selectedArea !== 'all') params.area = selectedArea;
    if (selectedStoreIds.length > 0) params.store_ids = selectedStoreIds.join(',');
    dashboardApi.area(params)
      .then(r => {
        setData(r.data);
        // Aggiorna la pool del multi-select solo quando NON è attivo un filtro store
        // (così la lista mostra sempre tutto il contesto Area/Brand/AM).
        if (Array.isArray(r.data?.available_stores)) {
          setAvailableStores(r.data.available_stores);
        }
        setLastRefresh(new Date());
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [effectiveRange, month, quarter, isAnnual, year, selectedAM, selectedBrand, selectedArea, selectedStoreIds, refreshKey]);

  // Derived chart data
  const storeRevenue = useMemo(() => {
    if (!data?.stores) return [];
    return data.stores
      .map(s => ({ name: s.display_name, actual: s.kpis?.total_sales?.actual || 0, budget: s.kpis?.total_sales?.budget || 0, ly: s.kpis?.total_sales?.last_year || 0 }))
      .filter(s => s.actual > 0)
      .sort((a, b) => b.actual - a.actual);
  }, [data]);

  const storeOccupancy = useMemo(() => {
    if (!data?.stores) return [];
    return data.stores
      .map(s => ({ name: s.display_name, value: s.kpis?.occupancy_pct?.actual || 0 }))
      .filter(s => s.value > 0)
      .sort((a, b) => b.value - a.value);
  }, [data]);

  const revenuePie = useMemo(() => {
    if (!data?.stores) return [];
    return data.stores
      .map(s => ({ name: s.display_name, value: s.kpis?.total_sales?.actual || 0 }))
      .filter(s => s.value > 0)
      .sort((a, b) => b.value - a.value);
  }, [data]);

  const radarData = useMemo(() => {
    if (!data?.stores) return [];
    const top5 = data.stores
      .filter(s => s.kpis?.total_sales?.actual > 0)
      .sort((a, b) => (b.kpis?.total_sales?.actual || 0) - (a.kpis?.total_sales?.actual || 0))
      .slice(0, 5);
    if (top5.length === 0) return [];
    const maxSales = Math.max(...top5.map(s => s.kpis?.total_sales?.actual || 1));
    const maxServices = Math.max(...top5.map(s => s.kpis?.services_count?.actual || 1));
    return top5.map(s => ({
      name: s.display_name,
      'Vendite': Math.round(((s.kpis?.total_sales?.actual || 0) / maxSales) * 100),
      'Servizi': Math.round(((s.kpis?.services_count?.actual || 0) / maxServices) * 100),
      'Occupazione': Math.round(s.kpis?.occupancy_pct?.actual || 0),
      'Clienti abituali': Math.round(s.kpis?.returning_client_rate?.actual || 0),
    }));
  }, [data]);

  // Total aggregates
  const totals = useMemo(() => {
    if (!data?.aggregated) return {};
    const ts = data.aggregated.total_sales || {};
    const sc = data.aggregated.services_count || {};
    return {
      revenue: ts.actual, budget: ts.budget, ly: ts.last_year,
      varBudget: ts.actual && ts.budget ? ts.actual - ts.budget : null,
      varLY: ts.actual && ts.last_year ? ts.actual - ts.last_year : null,
      services: sc.actual,
    };
  }, [data]);

  // Variazioni percentuali utili in più punti
  const varBudgetPct = useMemo(() => {
    if (!totals.budget || totals.varBudget == null) return null;
    return (totals.varBudget / totals.budget) * 100;
  }, [totals]);
  const varLyPct = useMemo(() => {
    if (!totals.ly || totals.varLY == null) return null;
    return (totals.varLY / totals.ly) * 100;
  }, [totals]);

  return (
    <TooltipProvider delayDuration={150}>
    <div className="p-4 md:p-8 max-w-[1920px]" data-testid="area-dashboard">
      {/* Header */}
      <div className="mb-6 flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold tracking-tight text-slate-900">Dashboard Area</h1>
          <p className="text-slate-500 mt-1">
            Performance per area · {data?.store_count || 0} negozi · {periodLabel}
            {lastRefresh && (
              <span className="text-xs text-slate-400 ml-2">
                · aggiornato {lastRefresh.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}
              </span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {/* Date range picker — la fonte di verità per la fetch. Quando
              il range coincide con un periodo canonico (mese/trimestre/
              anno intero) aggiorniamo anche `period`/`year` così i deep
              link cross-pagina e il MonthSelectorRich del negozio
              restano in sync. Per i range custom (non canonici), il
              backend autodetecta e usa la pipeline daily (refactor
              2026-05-13, idea_330bfb10f8d7). */}
          <DateRangePicker
            from={(effectiveRange || {}).from || ''}
            to={(effectiveRange || {}).to || ''}
            onChange={(f, t) => {
              if (isCanonicalRange(f, t)) {
                const conv = rangeToPeriod(f, t);
                setPeriod(conv.period);
                setYear(conv.year);
                setCustomRange(null);
              } else {
                setCustomRange({ from: f, to: t });
              }
            }}
            testIdPrefix="area-daterange"
          />
        </div>
      </div>

      {/* Filtri Area / Brand / AM / Negozi */}
      <div className="bg-white border border-slate-200 px-4 py-3 mb-6 flex flex-wrap items-center gap-3" data-testid="area-filters-bar">
        <span className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500 mr-1">Filtri</span>
        <Select value={selectedAM} onValueChange={setSelectedAM}>
          <SelectTrigger className="w-44 rounded-none" data-testid="am-filter"><SelectValue placeholder="Area Manager" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti gli Area Manager</SelectItem>
            {areaManagers.map(am => <SelectItem key={am.area_manager_id} value={am.area_manager_id}>{am.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={selectedBrand} onValueChange={setSelectedBrand}>
          <SelectTrigger className="w-44 rounded-none" data-testid="brand-filter"><SelectValue placeholder="Brand" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti i brand</SelectItem>
            {brands.map(b => <SelectItem key={b.brand_id} value={b.brand_id}>{b.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={selectedArea} onValueChange={setSelectedArea}>
          <SelectTrigger className="w-40 rounded-none" data-testid="area-filter"><SelectValue placeholder="Area" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutte le aree</SelectItem>
            {areas.map(a => <SelectItem key={a.area_id || a} value={a.name || a}>{a.name || a}</SelectItem>)}
          </SelectContent>
        </Select>
        <Popover open={storeFilterOpen} onOpenChange={setStoreFilterOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              className="rounded-none h-9 px-3 justify-between font-normal min-w-[12rem]"
              data-testid="stores-filter-trigger"
            >
              <span className="flex items-center gap-2 text-sm truncate">
                <StoreIcon className="w-4 h-4 text-slate-500" />
                {selectedStoreIds.length === 0
                  ? `Tutti i negozi (${availableStores.length})`
                  : `${selectedStoreIds.length} negozi selezionati`}
              </span>
              {selectedStoreIds.length > 0 && (
                <X
                  className="w-3.5 h-3.5 text-slate-500 hover:text-slate-900 ml-2"
                  data-testid="stores-filter-clear"
                  onClick={(e) => { e.stopPropagation(); setSelectedStoreIds([]); }}
                />
              )}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-72 p-0 rounded-none" align="end">
            <div className="p-2 border-b border-slate-100">
              <input
                type="text"
                value={storeFilterQuery}
                onChange={(e) => setStoreFilterQuery(e.target.value)}
                placeholder="Cerca negozio..."
                className="w-full px-2 py-1.5 text-sm border border-slate-200 rounded-none focus:outline-none focus:border-slate-900"
                data-testid="stores-filter-search"
              />
              <div className="flex justify-between mt-2 text-xs">
                <button
                  type="button"
                  className="text-slate-600 hover:text-slate-900 underline"
                  data-testid="stores-filter-select-all"
                  onClick={() => setSelectedStoreIds(availableStores.map(s => s.store_id))}
                >Seleziona tutti</button>
                <button
                  type="button"
                  className="text-slate-600 hover:text-slate-900 underline"
                  data-testid="stores-filter-deselect-all"
                  onClick={() => setSelectedStoreIds([])}
                >Deseleziona tutti</button>
              </div>
            </div>
            <div className="max-h-72 overflow-y-auto py-1">
              {availableStores.length === 0 && (
                <div className="px-3 py-4 text-xs text-slate-500">Nessun negozio nel contesto.</div>
              )}
              {[...availableStores]
                .sort((a, b) => (a.display_name || '').localeCompare(b.display_name || '', 'it', { sensitivity: 'base' }))
                .filter(s => !storeFilterQuery || s.display_name.toLowerCase().includes(storeFilterQuery.toLowerCase()))
                .map(s => {
                  const checked = selectedStoreIds.includes(s.store_id);
                  return (
                    <label
                      key={s.store_id}
                      className="flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-slate-50 cursor-pointer"
                      data-testid={`stores-filter-item-${s.store_id}`}
                    >
                      <Checkbox
                        checked={checked}
                        onCheckedChange={(v) => {
                          if (v) setSelectedStoreIds(prev => [...prev, s.store_id]);
                          else setSelectedStoreIds(prev => prev.filter(id => id !== s.store_id));
                        }}
                      />
                      <span className="truncate">{s.display_name}</span>
                    </label>
                  );
                })}
            </div>
          </PopoverContent>
        </Popover>
        <div className="ml-auto flex items-center gap-2">
          <CopyLinkButton testId="area-copy-link" />
          <SavedViewsControl
            scopeKey="area"
            currentPayload={{
              period, year,
              am: selectedAM, brand: selectedBrand, area: selectedArea,
              store_ids: selectedStoreIds,
            }}
            onApply={(p) => {
              if (p?.period) setPeriod(p.period);
              if (p?.year) setYear(p.year);
              if (p?.am) setSelectedAM(p.am);
              if (p?.brand) setSelectedBrand(p.brand);
              if (p?.area) setSelectedArea(p.area);
              setSelectedStoreIds(Array.isArray(p?.store_ids) ? p.store_ids : []);
            }}
            testIdPrefix="area-saved-views"
          />
        </div>
      </div>

      {!data ? (
        <div className="flex justify-center py-12"><div className="w-8 h-8 border-2 border-black border-t-transparent rounded-full animate-spin" /></div>
      ) : (
        <div className="space-y-6">
          {/* Hero strip — stessa cifra stilistica della Panoramica */}
          <div className="bg-slate-900 text-white p-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              <div>
                <p className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-400">Ricavo totale area</p>
                <p className="font-heading text-2xl md:text-4xl font-bold mt-1" data-testid="hero-revenue">{fmt(totals.revenue, 'currency')}</p>
                <p className="text-xs text-slate-400 mt-1">{periodLabel}</p>
              </div>
              <HeroDelta label="vs Budget" value={totals.varBudget} pct={varBudgetPct} base={totals.budget} />
              <HeroDelta label="vs Anno Prec." value={totals.varLY} pct={varLyPct} base={totals.ly} />
              {(() => {
                const occ = data?.aggregated?.occupancy_pct || {};
                const dB = (occ.actual != null && occ.budget != null) ? occ.actual - occ.budget : null;
                const dLy = (occ.actual != null && occ.last_year != null) ? occ.actual - occ.last_year : null;
                return (
                  <div data-testid="hero-occupancy">
                    <p className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-400">Tasso di occupazione</p>
                    <p className="font-heading text-2xl md:text-4xl font-bold mt-1">{occ.actual != null ? `${occ.actual.toFixed(1)}%` : '—'}</p>
                    <p className="text-xs text-slate-400 mt-1 flex flex-wrap gap-x-3 gap-y-0.5">
                      {dB != null && (
                        <span className={SEMAPHORE_TEXT[semaphoreFromPp(dB)]}>
                          vs Budget {dB > 0 ? '+' : ''}{dB.toFixed(1)}pp
                        </span>
                      )}
                      {dLy != null && (
                        <span className={SEMAPHORE_TEXT[semaphoreFromPp(dLy)]}>
                          vs A.P. {dLy > 0 ? '+' : ''}{dLy.toFixed(1)}pp
                        </span>
                      )}
                      {dB == null && dLy == null && <span>{data?.store_count || 0} negozi</span>}
                    </p>
                  </div>
                );
              })()}
            </div>
          </div>

          {/* Health Score area + Report AI affiancati (refactor layout
              2026-05-18, richiesta utente). */}
          {data?.aggregated && (() => {
            const areaHealth = healthFromKpiMap(data.aggregated);
            return (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="area-health-and-ai-row">
              <div
                className="bg-white border border-slate-200 px-4 md:px-5 py-4 flex items-center gap-4 md:gap-6"
                data-testid="area-health-score"
              >
                <HealthBadge health={areaHealth} size="lg" />
                <div className="min-w-0 flex-1">
                  <p className="text-[10px] uppercase tracking-[0.18em] font-semibold text-slate-500">Health score area</p>
                  <p className="font-heading text-base md:text-lg font-semibold mt-1 leading-tight">
                    {areaHealth.score == null
                      ? 'Non abbastanza dati di budget per questo periodo'
                      : areaHealth.band === 'green' ? 'Andamento buono'
                      : areaHealth.band === 'yellow' ? 'In linea con attenzione'
                      : 'Sotto target — intervento necessario'}
                  </p>
                  <div className="flex items-center gap-3 mt-1.5 text-xs text-slate-500 flex-wrap">
                    {areaHealth.score != null && (
                      <>
                        <span className="inline-flex items-center gap-1">
                          <span className="w-2 h-2 bg-emerald-500" /> {areaHealth.counts.green} verdi
                        </span>
                        <span className="inline-flex items-center gap-1">
                          <span className="w-2 h-2 bg-amber-500" /> {areaHealth.counts.yellow} gialli
                        </span>
                        <span className="inline-flex items-center gap-1">
                          <span className="w-2 h-2 bg-red-500" /> {areaHealth.counts.red} rossi
                        </span>
                        <span className="text-slate-300">·</span>
                      </>
                    )}
                    <span>{periodLabel}</span>
                    <span className="text-slate-300 hidden md:inline">·</span>
                    <span className="hidden md:inline">{data?.store_count || 0} negozi</span>
                  </div>
                </div>
              </div>
              <AIInsightsPanel
                cacheFetcher={aiCacheFetcher}
                generateFetcher={aiGenerateFetcher}
                forceFetcher={aiForceFetcher}
                pdfFetcher={aiPdfFetcher}
                currentSignature={data?.ai_signature}
                scope="area"
                entityId={[selectedAM || 'all', selectedBrand || 'all', selectedArea || 'all', String(year), isAnnual ? 'YEAR' : isQuarter ? `Q${quarter}` : `M${month}`].join('_')}
                title="Report AI"
                compact
                autoOpen
              />
              </div>
            );
          })()}

          {/* Aggregated KPI cards — tabs su sx + grafico su dx */}
          {data?.aggregated && (
            <KpiAggregatedTabs
              aggregated={data.aggregated}
              stores={data.stores || []}
              periodLabel={periodLabel}
              year={year}
              period={period}
            />
          )}

          {/* Store Comparison Table */}
          {data?.stores?.length > 0 && (
            <div className="bg-white border border-slate-200" data-testid="store-comparison">
              <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
                <h2 className="font-heading text-base font-semibold">Confronto Negozi — Dettaglio</h2>
                <span className="text-[10px] uppercase tracking-wider text-slate-400 hidden md:inline">
                  health score per riga
                </span>
              </div>
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full data-table">
                  <thead>
                    <tr>
                      <SortTh col="health_score" sort={sort} onSort={toggleSort} className="min-w-[180px] sticky left-0 z-20 bg-slate-50 border-r border-slate-200">Negozio</SortTh>
                      <SortTh col="revenue" sort={sort} onSort={toggleSort} align="right">Vendite</SortTh>
                      <SortTh col="var_budget" sort={sort} onSort={toggleSort} align="right">vs Budget</SortTh>
                      <SortTh col="var_ly" sort={sort} onSort={toggleSort} align="right">vs A.P.</SortTh>
                      <th className="text-left text-[10px] uppercase tracking-wider font-semibold text-slate-500 px-3 py-3 w-[160px]">Andamento 12 mesi</th>
                      <th className="text-left text-[10px] uppercase tracking-wider font-semibold text-slate-500 px-3 py-3 w-[120px]">Att vs Budget</th>
                      <SortTh col="services" sort={sort} onSort={toggleSort} align="right">Servizi</SortTh>
                      <SortTh col="occupancy" sort={sort} onSort={toggleSort} align="right">% Occ.</SortTh>
                      <SortTh col="avg_ticket" sort={sort} onSort={toggleSort} align="right">Ticket</SortTh>
                      <SortTh col="new_client" sort={sort} onSort={toggleSort} align="right">% Nuovi</SortTh>
                      <SortTh col="returning" sort={sort} onSort={toggleSort} align="right">% Abituali</SortTh>
                      <SortTh col="reviews" sort={sort} onSort={toggleSort} align="right">Recensioni</SortTh>
                    </tr>
                  </thead>
                  <tbody>
                    {(() => {
                      const rows = (data.stores || [])
                        .filter(s => s.kpis?.total_sales?.actual > 0)
                        .map(s => {
                          const k = s.kpis || {};
                          const sales = k.total_sales?.actual || 0;
                          const h = healthFromKpiMap(k);  // pre-computa Health per sort + render
                          return {
                            store: s,
                            k, h,
                            sales,
                            _varB: k.total_sales?.budget ? sales - k.total_sales.budget : null,
                            _varLY: k.total_sales?.last_year ? sales - k.total_sales.last_year : null,
                          };
                        });
                      // Scala globale per i mini-bar Att vs Budget: max tra
                      // attuale e budget di total_sales sulle righe visibili.
                      const maxBarValue = rows.reduce((m, r) => Math.max(
                        m, r.k.total_sales?.actual || 0, r.k.total_sales?.budget || 0,
                      ), 0);
                      const col = sort.col;
                      const dir = sort.dir === 'asc' ? 1 : -1;
                      const valueOf = (r) => ({
                        health_score: r.h?.score ?? -Infinity,
                        display_name: (r.store.display_name || '').toLowerCase(),
                        revenue: r.sales,
                        var_budget: r._varB ?? 0,
                        var_ly: r._varLY ?? 0,
                        services: r.k.services_count?.actual || 0,
                        occupancy: r.k.occupancy_pct?.actual || 0,
                        avg_ticket: r.k.avg_ticket?.actual || 0,
                        new_client: r.k.new_client_pct?.actual || 0,
                        returning: r.k.returning_client_rate?.actual || 0,
                        reviews: r.k.review_count?.actual || 0,
                      }[col]);
                      rows.sort((a, b) => {
                        const av = valueOf(a); const bv = valueOf(b);
                        if (typeof av === 'string') return av.localeCompare(bv) * dir;
                        return (av - bv) * dir;
                      });
                      return rows.map(({ store: s, k, h, sales, _varB, _varLY }) => {
                        const pctB = (k.total_sales?.budget && _varB != null) ? (_varB / k.total_sales.budget) * 100 : null;
                        const pctLY = (k.total_sales?.last_year && _varLY != null) ? (_varLY / k.total_sales.last_year) * 100 : null;
                        const trend = (s.monthly_trend || {}).total_sales || [];
                        return (
                        <tr key={s.store_id} data-testid={`area-store-row-${s.store_id}`} className="group">
                          <td className="sticky left-0 z-10 bg-white group-hover:bg-slate-50/80 border-r border-slate-200">
                            <div className="flex items-center gap-2.5">
                              <HealthBadge health={h} size="sm" />
                              <Link to={`/stores/${s.store_id}`} className="font-medium text-slate-900 hover:text-black hover:underline truncate">
                                {s.display_name}
                              </Link>
                            </div>
                          </td>
                          <td className="num">
                            <div className="flex items-center justify-end gap-1.5">
                              <SemDot status={k.total_sales?.semaforo || 'neutral'} />
                              {fmt(sales, 'currency')}
                            </div>
                          </td>
                          <td className="num">
                            {_varB != null ? (
                              <span className={`font-medium ${_varB >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>
                                {_varB > 0 ? '+' : ''}{fmt(_varB, 'currency')}
                                {pctB != null && (
                                  <span className="opacity-60 ml-1">({pctB > 0 ? '+' : ''}{pctB.toFixed(1)}%)</span>
                                )}
                              </span>
                            ) : '-'}
                          </td>
                          <td className="num">
                            {_varLY != null ? (
                              <span className={`font-medium ${_varLY >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>
                                {_varLY > 0 ? '+' : ''}{fmt(_varLY, 'currency')}
                                {pctLY != null && (
                                  <span className="opacity-60 ml-1">({pctLY > 0 ? '+' : ''}{pctLY.toFixed(1)}%)</span>
                                )}
                              </span>
                            ) : '-'}
                          </td>
                          <td className="px-3 py-2">
                            <TrendSparkline trend={trend} ariaLabel={`Andamento 12 mesi vendite ${s.display_name}`} />
                          </td>
                          <td className="px-3 py-2">
                            <ActualVsBudgetBars
                              actual={k.total_sales?.actual}
                              budget={k.total_sales?.budget}
                              semaforo={k.total_sales?.semaforo || 'neutral'}
                              maxBarValue={maxBarValue}
                            />
                          </td>
                          <td className="num">{fmt(k.services_count?.actual, 'integer')}</td>
                          <td className="num">
                            <div className="flex items-center justify-end gap-1.5">
                              <SemDot status={k.occupancy_pct?.semaforo || 'neutral'} />
                              {fmt(k.occupancy_pct?.actual, 'percentage')}
                            </div>
                          </td>
                          <td className="num">{fmt(k.avg_ticket?.actual, 'currency')}</td>
                          <td className="num">{fmt(k.new_client_pct?.actual, 'percentage')}</td>
                          <td className="num">{fmt(k.returning_client_rate?.actual, 'percentage')}</td>
                          <td className="num">{fmt(k.review_count?.actual, 'integer')}</td>
                        </tr>
                        );
                      });
                    })()}
                  </tbody>
                </table>
              </div>

              {/* Mobile: cards ordinate per health score (peggiori prima),
                  con i 4 KPI chiave necessari a triare gli interventi senza
                  dover aprire la pagina del singolo negozio. */}
              <ul className="md:hidden divide-y divide-slate-100" data-testid="area-store-cards-mobile">
                {(data.stores || [])
                  .filter(s => s.kpis?.total_sales?.actual > 0)
                  .map(s => ({ s, h: healthFromKpiMap(s.kpis || {}) }))
                  .sort((a, b) => {
                    // null health rolls to the bottom
                    const av = a.h.score == null ? 999 : a.h.score;
                    const bv = b.h.score == null ? 999 : b.h.score;
                    return av - bv;
                  })
                  .map(({ s, h }) => {
                    const k = s.kpis || {};
                    const sales = k.total_sales?.actual || 0;
                    const vB = k.total_sales?.budget ? sales - k.total_sales.budget : null;
                    const pctB = (k.total_sales?.budget && vB != null) ? (vB / k.total_sales.budget) * 100 : null;
                    return (
                      <li key={s.store_id} data-testid={`area-store-card-${s.store_id}`}>
                        <Link
                          to={`/stores/${s.store_id}`}
                          className="flex items-center gap-3 px-4 py-3.5 hover:bg-slate-50 transition-colors"
                        >
                          <HealthBadge health={h} size="md" />
                          <div className="min-w-0 flex-1">
                            <p className="font-heading font-semibold text-sm text-slate-900 truncate">{s.display_name}</p>
                            <p className="text-[11px] text-slate-500 mt-0.5 tabular-nums">
                              {fmt(sales, 'currency')}
                              {pctB != null && (
                                <span className={`ml-2 ${pctB >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>
                                  {pctB > 0 ? '+' : ''}{pctB.toFixed(1)}% vs budget
                                </span>
                              )}
                            </p>
                            <p className="text-[10px] text-slate-400 mt-0.5">
                              Occ. {fmt(k.occupancy_pct?.actual, 'percentage')}
                              {' · '}Ticket {fmt(k.avg_ticket?.actual, 'currency')}
                              {' · '}{fmt(k.services_count?.actual, 'integer')} servizi
                            </p>
                          </div>
                        </Link>
                      </li>
                    );
                  })}
              </ul>
            </div>
          )}

          {/* Redditività dell'area / subset di negozi */}
          {data?.profitability && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <ProfitabilitySection
                profitability={data.profitability}
                periodLabel={periodLabel}
                title="Redditività"
                testId="area-profitability-section"
              />
              <TopCostsChart
                profitability={data.profitability}
                title="Top voci di costo"
                testId="area-top-costs-chart"
              />
            </div>
          )}
        </div>
      )}
    </div>
    </TooltipProvider>
  );
}

function HeroDelta({ label, value, pct, base }) {
  if (value == null) {
    return (
      <div>
        <p className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-400">{label}</p>
        <p className="font-heading text-2xl md:text-4xl font-bold text-slate-500 mt-1">—</p>
      </div>
    );
  }
  const sem = semaphoreFromPct(pct) || (value >= 0 ? 'green' : 'red');
  const tone = SEMAPHORE_TEXT[sem];
  const Icon = value >= 0 ? TrendingUp : TrendingDown;
  return (
    <div data-testid={`hero-delta-${label?.replace(/\s+/g, '-').toLowerCase()}`}>
      <p className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-400">{label}</p>
      <p className={`font-heading text-2xl md:text-4xl font-bold mt-1 flex items-center gap-2 ${tone}`}>
        <Icon className="w-6 h-6 opacity-70" />
        {value > 0 ? '+' : ''}{fmt(value, 'currency')}
      </p>
      <p className="text-xs text-slate-400 mt-1">
        {pct != null && <span className={tone}>{pct > 0 ? '+' : ''}{pct.toFixed(1)}%</span>}
        {base != null && <> · base {fmt(base, 'currency')}</>}
      </p>
    </div>
  );
}

// Definizione gruppi KPI Aggregati come da wireframe utente.
// Per ogni tab, `kpis` elenca le metriche da mostrare a sinistra e `chartKey`
// indica il KPI principale da visualizzare nel chart per-negozio a destra.
// `Vendite` è in cima (refactor 2026-05-11, idea_389945efd628): è la metrica
// più importante per OPS/AM ed è la prima che cercano di mattina.
const KPI_GROUPS = [
  {
    id: 'vendite',
    label: 'Vendite',
    icon: '€',
    kpis: ['total_sales', 'hourly_revenue', 'avg_ticket'],
    chartKey: 'total_sales',
    chartLabel: 'Vendite totali per negozio',
  },
  {
    id: 'servizi',
    label: 'Servizi',
    icon: '✂',
    kpis: ['services_count', 'service_revenue'],
    chartKey: 'service_revenue',
    chartLabel: 'Ricavo servizi per negozio',
  },
  {
    id: 'prodotti',
    label: 'Prodotti',
    icon: '🛍',
    kpis: ['products_count', 'product_revenue'],
    chartKey: 'product_revenue',
    chartLabel: 'Ricavo prodotti per negozio',
  },
  {
    id: 'clienti',
    label: 'Clienti',
    icon: '👥',
    kpis: ['occupancy_pct', 'new_client_pct', 'review_count'],
    chartKey: 'occupancy_pct',
    chartLabel: '% Occupazione per negozio',
  },
];

// Refactor 2026-05-13b (richiesta utente, secondo round): tab orizzontali
// per categoria (Vendite | Servizi | Prodotti | Clienti). Dentro il tab,
// griglia di sub-KPI card con dati aggregati area (totale, vs budget,
// vs anno prec, top/bottom, conteggio negozi, mini-sparkline 12 mesi
// se il backend espone aggregated_trend, altrimenti omessa).

function KpiAggregatedTabs({ aggregated, stores, periodLabel, year, period }) {
  const [activeId, setActiveId] = useState(KPI_GROUPS[0].id);
  const activeGroup = KPI_GROUPS.find(g => g.id === activeId) || KPI_GROUPS[0];

  return (
    <div className="bg-white border border-slate-200" data-testid="aggregated-kpis">
      <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between flex-wrap gap-2">
        <span className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500">KPI aggregati · {periodLabel}</span>
        <div className="flex items-center gap-3 text-[10px] text-slate-400 flex-wrap">
          <span className="flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-emerald-600 inline-block" /> sopra budget</span>
          <span className="flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-amber-500 inline-block" /> entro -5%</span>
          <span className="flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-red-600 inline-block" /> sotto budget</span>
        </div>
      </div>

      {/* Tab "block" per CATEGORIA (Opzione C — card grandi con preview totale).
       *  Stile identico al MonthSelectorRich della pagina negozio:
       *  bg-slate-900 + testo bianco quando selezionato. */}
      <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-y divide-slate-100 border-t border-b border-slate-100" role="tablist">
        {KPI_GROUPS.map(g => {
          const isActive = activeId === g.id;
          const headline = aggregated[g.kpis[0]] || {};
          const isPctHead = headline.format === 'percentage' || headline.format === 'pct';
          const fmtH = (v) => v == null ? '—'
            : isPctHead ? `${Number(v).toFixed(1)}%`
            : fmt(v, headline.format || 'currency');
          const varBudgetPct = (headline.actual != null && headline.budget)
            ? ((headline.actual - headline.budget) / headline.budget) * 100 : null;
          const deltaCls = varBudgetPct == null
            ? (isActive ? 'text-slate-400' : 'text-slate-400')
            : varBudgetPct >= 0 ? (isActive ? 'text-emerald-300' : 'text-emerald-700')
            : varBudgetPct >= -5 ? (isActive ? 'text-amber-300' : 'text-amber-700')
            : (isActive ? 'text-red-300' : 'text-red-700');
          return (
            <button
              key={g.id} type="button" role="tab" aria-selected={isActive}
              onClick={() => setActiveId(g.id)}
              data-testid={`kpi-tab-${g.id}`}
              className={`text-left p-4 transition-colors ${
                isActive
                  ? 'bg-slate-900 text-white'
                  : 'bg-white hover:bg-slate-50/60'
              }`}
            >
              <div className={`text-[10px] uppercase tracking-[0.18em] font-semibold mb-2 ${isActive ? 'text-slate-300' : 'text-slate-500'}`}>
                {g.label}
              </div>
              <div className={`font-heading text-xl font-bold tabular-nums leading-tight ${isActive ? 'text-white' : 'text-slate-900'}`}>
                {fmtH(headline.actual)}
              </div>
              <div className="flex items-baseline gap-1.5 mt-1 text-[11px]">
                <span className={`tabular-nums font-mono font-medium ${deltaCls}`}>
                  {varBudgetPct != null ? `${varBudgetPct > 0 ? '+' : ''}${varBudgetPct.toFixed(1)}%` : '—'}
                </span>
                <span className={isActive ? 'text-slate-400' : 'text-slate-400'}>vs budget</span>
              </div>
            </button>
          );
        })}
      </div>

      {/* Griglia delle sub-KPI card per la categoria attiva */}
      <div className="p-5" role="tabpanel" data-testid={`kpi-panel-${activeId}`}>
        <div className={`grid gap-4 ${
          activeGroup.kpis.length === 2 ? 'md:grid-cols-2'
          : activeGroup.kpis.length === 3 ? 'md:grid-cols-3'
          : 'md:grid-cols-3 lg:grid-cols-4'
        }`}>
          {activeGroup.kpis.map(k => (
            <SubKpiCard
              key={k} kpiKey={k} kpi={aggregated[k]} stores={stores}
              year={year} period={period}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

/** Card di un sub-KPI nell'area-dashboard. Mostra:
 *  - Header: label + formula (tooltip)
 *  - Totale grosso (semaforo a sinistra)
 *  - vs Budget (% + assoluta, colorato)
 *  - vs Anno Prec. (% + assoluta)
 *  - Footer: # negozi con dati · Top / Bottom store per quel KPI
 */
function SubKpiCard({ kpiKey, kpi, stores, year, period }) {
  const isPct = kpi?.format === 'percentage' || kpi?.format === 'pct';
  const fmtV = (v) => {
    if (v == null) return '—';
    if (isPct) return `${Number(v).toFixed(1)}%`;
    return fmt(v, kpi?.format || 'currency');
  };
  // Deep-link allo store con stesso periodo selezionato. `usePersistedPeriod`
  // legge prima dai search params, quindi atterrando sulla Dashboard Negozio
  // si vede subito il KPI del mese corretto.
  const storeLink = (storeId) => {
    const qs = new URLSearchParams();
    if (period) qs.set('period', String(period));
    if (year) qs.set('year', String(year));
    return `/stores/${storeId}${qs.toString() ? `?${qs.toString()}` : ''}`;
  };
  const semDot = kpi?.semaforo === 'green' ? 'bg-emerald-500'
    : kpi?.semaforo === 'red' ? 'bg-red-500'
    : kpi?.semaforo === 'yellow' ? 'bg-amber-400'
    : 'bg-slate-300';

  const varBudgetPct = (kpi?.actual != null && kpi?.budget) ? ((kpi.actual - kpi.budget) / kpi.budget) * 100 : null;
  const varBudgetAbs = (kpi?.actual != null && kpi?.budget != null) ? kpi.actual - kpi.budget : null;
  const varLyPct = (kpi?.actual != null && kpi?.last_year) ? ((kpi.actual - kpi.last_year) / kpi.last_year) * 100 : null;
  const varLyAbs = (kpi?.actual != null && kpi?.last_year != null) ? kpi.actual - kpi.last_year : null;

  // Top/Bottom per-store (sul KPI singolo, non sull'aggregato)
  const ranking = useMemo(() => {
    const rows = (stores || []).map(s => {
      const k = (s.kpis || {})[kpiKey] || {};
      return { name: s.display_name, actual: k.actual, store_id: s.store_id };
    }).filter(r => r.actual != null);
    rows.sort((a, b) => (b.actual ?? -Infinity) - (a.actual ?? -Infinity));
    return { top: rows[0] || null, bottom: rows[rows.length - 1] || null, count: rows.length };
  }, [stores, kpiKey]);

  const deltaCls = (pct) => pct == null ? 'text-slate-400'
    : pct >= 0 ? 'text-emerald-700'
    : pct >= -5 ? 'text-amber-700'
    : 'text-red-700';

  const formula = KPI_FORMULAS[kpiKey];

  return (
    <div className="border border-slate-200 p-4 flex flex-col gap-2" data-testid={`kpi-card-${kpiKey}`}>
      <div className="flex items-start justify-between gap-2">
        <p className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 leading-tight" title={formula || undefined}>
          {kpi?.label || kpiKey}
        </p>
        <span className={`w-2 h-2 rounded-full ${semDot} flex-shrink-0 mt-1`} />
      </div>
      <p className="font-heading text-2xl font-bold text-slate-900 tabular-nums leading-tight">
        {fmtV(kpi?.actual)}
      </p>

      <div className="flex flex-col gap-1 mt-1">
        <div className="flex items-baseline justify-between gap-2 text-[11px]">
          <span className="text-slate-500">vs Budget</span>
          <span className={`tabular-nums font-mono font-medium ${deltaCls(varBudgetPct)}`}>
            {varBudgetPct != null ? (
              <>
                {varBudgetPct > 0 ? '+' : ''}{varBudgetPct.toFixed(1)}%
                {varBudgetAbs != null && (
                  <span className="text-slate-400 ml-1">({varBudgetAbs > 0 ? '+' : ''}{isPct ? `${varBudgetAbs.toFixed(1)}pp` : fmtV(varBudgetAbs)})</span>
                )}
              </>
            ) : '—'}
          </span>
        </div>
        <div className="flex items-baseline justify-between gap-2 text-[11px]">
          <span className="text-slate-500">vs Anno Prec.</span>
          <span className={`tabular-nums font-mono font-medium ${varLyPct != null && varLyPct >= 0 ? 'text-emerald-700' : varLyPct != null ? 'text-red-700' : 'text-slate-400'}`}>
            {varLyPct != null ? (
              <>
                {varLyPct > 0 ? '+' : ''}{varLyPct.toFixed(1)}%
                {varLyAbs != null && (
                  <span className="text-slate-400 ml-1">({varLyAbs > 0 ? '+' : ''}{isPct ? `${varLyAbs.toFixed(1)}pp` : fmtV(varLyAbs)})</span>
                )}
              </>
            ) : '—'}
          </span>
        </div>
      </div>

      <div className="border-t border-slate-100 pt-2 mt-1 flex flex-col gap-1 text-[10px]">
        {ranking.top && (
          <div className="flex justify-between gap-2">
            <span className="text-emerald-600 font-semibold">↑ Top</span>
            <Link
              to={storeLink(ranking.top.store_id)}
              data-testid={`kpi-card-${kpiKey}-top`}
              className="text-slate-700 hover:text-black hover:underline truncate"
            >
              {ranking.top.name} <span className="text-slate-400 font-mono ml-1">{fmtV(ranking.top.actual)}</span>
            </Link>
          </div>
        )}
        {ranking.bottom && ranking.bottom.store_id !== ranking.top?.store_id && (
          <div className="flex justify-between gap-2">
            <span className="text-red-600 font-semibold">↓ Bottom</span>
            <Link
              to={storeLink(ranking.bottom.store_id)}
              data-testid={`kpi-card-${kpiKey}-bottom`}
              className="text-slate-700 hover:text-black hover:underline truncate"
            >
              {ranking.bottom.name} <span className="text-slate-400 font-mono ml-1">{fmtV(ranking.bottom.actual)}</span>
            </Link>
          </div>
        )}
        <div className="text-slate-400">{ranking.count} negozi con dati</div>
      </div>
    </div>
  );
}


/** Mini-sparkline 12 mesi (Attuale solido + Budget tratteggiato viola).
 *  Riusato nella tabella "Confronto Negozi — Dettaglio" e ovunque servisse
 *  un trend a 12 mesi compatto. `trend` deve essere un array di
 *  `{actual, budget}` (12 elementi in ordine cronologico). Quando l'ultimo
 *  attuale è sotto budget di >5% la linea diventa rossa per richiamo. */
function TrendSparkline({ trend, ariaLabel }) {
  if (!Array.isArray(trend) || trend.length < 2) {
    return <span className="text-[10px] text-slate-300 italic">trend insuff.</span>;
  }
  const W = 140; const H = 32; const PAD = 2;
  const actuals = trend.map(t => t.actual);
  const budgets = trend.map(t => t.budget);
  const allVals = [...actuals, ...budgets].filter(v => v != null);
  if (allVals.length < 2) {
    return <span className="text-[10px] text-slate-300 italic">trend insuff.</span>;
  }
  const yMin = Math.min(...allVals);
  const yMax = Math.max(...allVals);
  const yRange = yMax - yMin || 1;
  const x = (i) => PAD + (i / (trend.length - 1)) * (W - 2 * PAD);
  const y = (v) => H - PAD - ((v - yMin) / yRange) * (H - 2 * PAD);
  const buildPath = (vals) => {
    let d = ''; let drawn = false;
    vals.forEach((v, i) => {
      if (v == null) { drawn = false; return; }
      d += `${drawn ? 'L' : 'M'}${x(i).toFixed(1)},${y(v).toFixed(1)}`;
      drawn = true;
    });
    return d;
  };
  const lastIdx = actuals.map((v, i) => v != null ? i : -1).filter(i => i >= 0).pop();
  const lastAct = lastIdx != null ? actuals[lastIdx] : null;
  const lastBud = lastIdx != null ? budgets[lastIdx] : null;
  const lineColor = (lastAct != null && lastBud != null && lastBud > 0
    && (lastAct - lastBud) / lastBud < -0.05) ? '#dc2626' : '#0f172a';
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-8" preserveAspectRatio="none" aria-label={ariaLabel}>
      {buildPath(budgets) && (
        <path d={buildPath(budgets)} stroke="#8b5cf6" strokeWidth="1" strokeDasharray="2 2" fill="none" />
      )}
      {buildPath(actuals) && (
        <path d={buildPath(actuals)} stroke={lineColor} strokeWidth="1.6" fill="none" />
      )}
    </svg>
  );
}

/** Due barre orizzontali sovrapposte: Attuale (semaforo) sopra, Budget
 *  (viola) sotto. Scala condivisa via `maxBarValue` per allineare i
 *  confronti tra righe della stessa tabella. */
function ActualVsBudgetBars({ actual, budget, semaforo, maxBarValue }) {
  if (maxBarValue <= 0 || (actual == null && budget == null)) {
    return <span className="text-[10px] text-slate-300 italic">—</span>;
  }
  const aPct = actual != null ? (actual / maxBarValue) * 100 : 0;
  const bPct = budget != null ? (budget / maxBarValue) * 100 : 0;
  const barColor = semaforo === 'green' ? 'bg-emerald-500'
    : semaforo === 'red' ? 'bg-red-500'
    : semaforo === 'yellow' ? 'bg-amber-400'
    : 'bg-slate-300';
  return (
    <div className="min-w-[80px]">
      <div className="bg-slate-100 h-1.5 relative overflow-hidden">
        <span className={`absolute left-0 top-0 bottom-0 ${barColor}`} style={{ width: `${Math.min(100, aPct)}%` }} />
      </div>
      <div className="bg-slate-100 h-1.5 relative overflow-hidden mt-0.5">
        <span className="absolute left-0 top-0 bottom-0 bg-violet-400" style={{ width: `${Math.min(100, bPct)}%` }} />
      </div>
    </div>
  );
}
